/**
 * Shared style tokens for the Campaigns pages.
 *
 * Centralizes the design-system primitives used across the campaign flow so
 * every screen, card, and section shares the same typography, spacing, and
 * tonal-surface treatment.
 */

import type { SxProps, Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { NAVY, RADIUS_CARD, SHADOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

const FONT_FAMILY = 'Jost, sans-serif';

export const pageContainerSx: SxProps<Theme> = {
  px: { xs: 2, lg: 4 },
  py: 3,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: FONT_FAMILY,
};

export const sectionTitleSx: SxProps<Theme> = {
  fontFamily: FONT_FAMILY,
  fontWeight: 600,
  color: NAVY,
  fontSize: '1.125rem',
  lineHeight: 1.3,
};

export const sectionSubtitleSx: SxProps<Theme> = {
  fontFamily: FONT_FAMILY,
  fontWeight: 400,
  color: alpha(NAVY, 0.65),
  fontSize: '0.9rem',
  mt: 0.5,
};

export const labelSx: SxProps<Theme> = {
  fontFamily: FONT_FAMILY,
  fontWeight: 600,
  color: alpha(NAVY, 0.55),
  fontSize: '0.75rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

export const bodyTextSx: SxProps<Theme> = {
  fontFamily: FONT_FAMILY,
  color: NAVY,
  fontSize: '0.95rem',
};

export const surfaceCardSx: SxProps<Theme> = {
  backgroundColor: SURFACE_CONTAINER_LOWEST,
  borderRadius: RADIUS_CARD,
  boxShadow: SHADOW,
  p: 3,
};

export const interactiveCardSx: SxProps<Theme> = {
  ...surfaceCardSx,
  cursor: 'pointer',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(21, 24, 58, 0.12)',
  },
};

export const sectionGap = 3;
